import { useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_5ecc19a3-715e-40b2-bee7-8ae4aeb4c8cb/artifacts/ladwjbmi_4926BBCA-F27B-484F-9467-294F35FF2347.PNG';

function RevealText({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div
        className={className}
        initial={{ y: '100%', opacity: 0 }}
        animate={isInView ? { y: '0%', opacity: 1 } : {}}
        transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
}

const capabilities = [
  {
    id: 'brand',
    number: '01',
    title: 'Brand Creation',
    headline: 'Identities built for market dominance.',
    description: 'We develop brand systems from zero — positioning, visual identity, naming, packaging architecture, and narrative strategy. Each brand is engineered for differentiation and consumer resonance in competitive U.S. markets.',
    deliverables: ['Brand Strategy & Positioning', 'Visual Identity Systems', 'Packaging Architecture', 'Naming & Trademark', 'Brand Guidelines & Governance'],
  },
  {
    id: 'product',
    number: '02',
    title: 'Product Development',
    headline: 'From raw concept to shelf-ready.',
    description: 'Our product pipeline transforms market signals into tangible consumer goods. We manage the complete lifecycle — concept validation, formulation, prototyping, compliance, and production management — with rigorous quality control at every gate.',
    deliverables: ['Market Gap Analysis', 'Product Formulation & Prototyping', 'Regulatory Compliance', 'Quality Assurance Systems', 'Production Scale-Up'],
  },
  {
    id: 'supply',
    number: '03',
    title: 'Supply Chain',
    headline: "Infrastructure that doesn't break.",
    description: 'We architect and operate supply chains designed for velocity and resilience. From sourcing to final-mile delivery, every link is monitored, optimized, and stress-tested. We work with a global supplier network while maintaining domestic fulfillment standards.',
    deliverables: ['Supplier Sourcing & Vetting', 'Logistics Orchestration', 'Inventory Management', 'Warehousing & Fulfillment', 'Cost Optimization'],
  },
  {
    id: 'commerce',
    number: '04',
    title: 'Digital Commerce',
    headline: 'Full-stack channel execution.',
    description: 'We deploy and manage brands across every relevant commerce channel — DTC, marketplace, retail, and hybrid. Our systems handle storefront architecture, catalog management, paid acquisition, CRO, and retention with a unified data layer across all touchpoints.',
    deliverables: ['Storefront Architecture', 'Marketplace Management', 'Performance Marketing', 'Conversion Optimization', 'Retention & Lifecycle'],
  },
];

/* ─── CAPABILITIES HERO ─── */
function CapabilitiesHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  return (
    <section ref={ref} className="relative min-h-[80vh] flex items-end bg-xmx-black px-6 sm:px-12 pb-20 sm:pb-28 overflow-hidden">
      {/* Accent line */}
      <motion.div
        className="absolute top-0 left-6 sm:left-12 w-[1px] h-40 bg-gradient-to-b from-xmx-accent/60 to-transparent"
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{ duration: 1.2, delay: 0.5 }}
        style={{ transformOrigin: 'top' }}
      />

      <motion.div className="max-w-6xl mx-auto w-full pt-32" style={{ y: textY }}>
        <motion.span
          className="font-mono text-[10px] tracking-[0.3em] uppercase text-xmx-accent block mb-8"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Capabilities
        </motion.span>

        <motion.h1
          className="font-cabinet text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-bold text-xmx-white max-w-4xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          A controlled system.
          <br />
          <span className="text-xmx-grey">Four disciplines. One operator.</span>
        </motion.h1>
      </motion.div>
    </section>
  );
}

/* ─── CAPABILITY DETAIL SECTION ─── */
function CapabilityDetail({ capability, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isEven = index % 2 === 0;

  return (
    <section
      ref={ref}
      className={`relative py-24 sm:py-36 px-6 sm:px-12 ${isEven ? 'bg-xmx-black' : 'bg-xmx-dark'}`}
      data-testid={`capability-detail-${capability.id}`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Left column: number + title */}
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="font-mono text-5xl sm:text-7xl font-light text-xmx-accent/20 block mb-4">
              {capability.number}
            </span>
            <h2 className="font-cabinet text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-xmx-white uppercase">
              {capability.title}
            </h2>
          </motion.div>

          {/* Right column: content */}
          <div className="lg:col-span-8 lg:pt-6">
            <motion.h3
              className="font-cabinet text-xl sm:text-2xl tracking-tight font-medium text-xmx-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              {capability.headline}
            </motion.h3>

            <motion.p
              className="font-mono text-sm leading-relaxed text-xmx-grey mb-10 max-w-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              {capability.description}
            </motion.p>

            {/* Deliverables */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-5">
                Deliverables
              </span>
              <div className="space-y-3">
                {capability.deliverables.map((item, di) => (
                  <motion.div
                    key={item}
                    className="flex items-center gap-4 group"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + di * 0.06 }}
                  >
                    <span className="w-1.5 h-1.5 bg-xmx-accent/50 group-hover:bg-xmx-accent transition-colors duration-300 flex-shrink-0" />
                    <span className="font-mono text-sm text-xmx-grey group-hover:text-xmx-white transition-colors duration-300">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── PROCESS SECTION ─── */
function ProcessSection() {
  const steps = [
    { label: 'Identify', desc: 'Market signal detection' },
    { label: 'Develop', desc: 'Product & brand creation' },
    { label: 'Launch', desc: 'Multi-channel deployment' },
    { label: 'Scale', desc: 'Growth & optimization' },
  ];

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section ref={ref} className="relative bg-xmx-black py-28 sm:py-40 px-6 sm:px-12 border-t border-white/[0.04]" data-testid="process-section">
      <div className="max-w-6xl mx-auto">
        <RevealText>
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-xmx-muted block mb-16 sm:mb-24">
            Our Process
          </span>
        </RevealText>

        <div className="relative">
          {/* Connecting line */}
          <motion.div
            className="hidden lg:block absolute top-6 left-0 right-0 h-[1px] bg-white/[0.06]"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: 'left' }}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
            {steps.map((step, i) => (
              <motion.div
                key={step.label}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="w-3 h-3 bg-xmx-accent mb-6 hidden lg:block" />
                <span className="font-mono text-[10px] tracking-[0.2em] text-xmx-accent block mb-3">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-cabinet text-2xl sm:text-3xl tracking-tight font-bold text-xmx-white uppercase mb-2">
                  {step.label}
                </h3>
                <p className="font-mono text-xs text-xmx-muted">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── CAPABILITIES FOOTER ─── */
function CapabilitiesFooter() {
  return (
    <footer className="bg-xmx-black border-t border-white/[0.06] px-6 sm:px-12 py-10" data-testid="capabilities-footer">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <img src={LOGO_URL} alt="XMX Ventures" className="h-8 w-auto opacity-40" />
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-xmx-muted">
          {new Date().getFullYear()} XMX Ventures
        </span>
      </div>
    </footer>
  );
}

export default function Capabilities() {
  return (
    <div data-testid="capabilities-page" className="bg-xmx-black">
      <CapabilitiesHero />
      {capabilities.map((cap, i) => (
        <CapabilityDetail key={cap.id} capability={cap} index={i} />
      ))}
      <ProcessSection />
      <CapabilitiesFooter />
    </div>
  );
}
